<script setup lang="ts">
import { computed } from "vue"
import { useI18n } from "vue-i18n"

import { Button, Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from "@/shared/ui"
import { useRecordsStore } from "../model/store"

defineOptions({ name: "CellChooserSheet" })

const store = useRecordsStore()
const { t, d } = useI18n()

const open = computed(() => store.activeSheet === "chooser")

const masterName = computed(
  () => store.workingMasters.find((m) => m.id === store.chooserMasterId)?.name ?? "—",
)

const dateLabel = computed(() => {
  const [year, month, day] = store.selectedDate.split("-").map(Number)
  const dt = new Date(year, month - 1, day)
  return d(dt, { month: "long", day: "numeric" })
})

function handleCreate(): void {
  if (!store.chooserMasterId) return
  store.setActiveMaster(store.chooserMasterId)
  store.setCreateStart(store.chooserStartMin)
  store.openCreate()
}

function handleBlock(): void {
  if (!store.chooserMasterId) return
  store.openCreateBlock(store.chooserMasterId, store.chooserStartMin)
}
</script>

<template>
  <Sheet :open="open" @update:open="(v) => { if (!v) store.closeSheet() }">
    <SheetContent side="bottom" :close-aria-label="t('records.chooser.close')">
      <SheetHeader>
        <SheetTitle>{{ t("records.chooser.title") }}</SheetTitle>
      </SheetHeader>

      <p class="chooser-sheet__context">{{ masterName }} · {{ dateLabel }}</p>

      <SheetFooter class="chooser-sheet__footer">
        <Button
          data-testid="btn-chooser-create"
          class="w-full"
          @click="handleCreate"
        >
          {{ t("records.chooser.createBooking") }}
        </Button>

        <Button
          data-testid="btn-chooser-block"
          variant="outline"
          class="w-full"
          @click="handleBlock"
        >
          {{ t("records.chooser.blockTime") }}
        </Button>

        <Button
          data-testid="btn-chooser-cancel"
          variant="ghost"
          class="w-full"
          @click="store.closeSheet()"
        >
          {{ t("records.chooser.cancel") }}
        </Button>
      </SheetFooter>
    </SheetContent>
  </Sheet>
</template>

<style scoped>
.chooser-sheet__context {
  padding: 12px 0;
  font-size: 0.875rem;
  color: var(--muted-foreground);
}

.chooser-sheet__footer {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 12px;
}
</style>
